/**
 * Step 5: Post Assessment Component
 * 
 * Interview logistics configuration with:
 * - Interview mode selection (Online/In-Person)
 * - Interviewer selection (card-based)
 * - External guest management
 * - Auto-save via React Hook Form
 * - Real-time validation
 * - Progress tracking
 * 
 * UI identical to V2, form management uses React Hook Form
 */

import React, { useMemo, useCallback } from 'react';
import { Card, Alert, Typography } from 'antd';
import { useFormContext, Controller } from 'react-hook-form';
import { JobPostFormData } from '../../schemas/jobPostValidation';
import { calculateStep5CompletionPercentage, validateStep5Complete } from '../../hooks/useFormValidation';
import InterviewModeSelector from './components/InterviewModeSelector';
import InterviewerSelector from './components/InterviewerSelector';
import InterviewGuestManager from './components/InterviewGuestManager';
import { PostAssessmentState, transformStoreToStep5, transformFormToStep5Data } from './utils/formTransformers';
import StepFooter from '../../components/StepFooter';
import { PersistenceStatus } from '../../hooks/useFormPersistence';
import './index.scss';

interface Step5PostAssessmentProps {
  currentStep: number;
  totalSteps: number;
  prompt: string;
  isGenerating: boolean;
  onPromptChange: (value: string) => void;
  onGenerate: () => void;
  onPrevious: () => void;
  onNext: () => void;
  isPreviousDisabled?: boolean;
  isNextDisabled?: boolean;
  stepTitles: string[];
  isNavigating?: boolean;
  saveStatus: PersistenceStatus;
}

const Step5PostAssessment: React.FC<Step5PostAssessmentProps> = ({
  currentStep,
  totalSteps,
  prompt,
  isGenerating,
  onPromptChange,
  onGenerate,
  onPrevious,
  onNext,
  isPreviousDisabled = false,
  isNextDisabled = false,
  stepTitles,
  isNavigating = false,
  saveStatus,
}) => {
  const { control, watch, setValue } = useFormContext<JobPostFormData>();
  
  // Watch form values
  const step5Data = watch('step5');
  const mode = watch('mode') as 'new' | 'edit' | 'clone' | undefined;
  const isEditMode = mode === 'edit';
  
  // Convert form data to PostAssessmentState for local management
  const state = useMemo(() => {
    return transformStoreToStep5(step5Data);
  }, [step5Data]);

  // Calculate completion state
  const completionState = useMemo(() => {
    return {
      completed: validateStep5Complete(step5Data),
      percentage: calculateStep5CompletionPercentage(step5Data),
    };
  }, [step5Data]);

  // Handle state changes
  const handleStateChange = useCallback((updates: Partial<PostAssessmentState>) => {
    // If switching from online to in-person, clear interviewers and guests
    if (updates.interviewMode === 'in-person' && state.interviewMode === 'online') {
      updates.interviewers = [];
      updates.interviewGuests = [];
    }
    
    const newState = { ...state, ...updates };
    const step5StoreData = transformFormToStep5Data(newState);
    setValue('step5', step5StoreData, { shouldDirty: true });
  }, [state, setValue]);

  // Determine if interviewers are required
  const interviewersRequired = state.interviewMode === 'online';

  return (
    <div className="step5-container">
      <Card
        className="step5-card"
        title={
          <div>
            <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 4, fontWeight: 600 }}>
              Post Assessment
            </Typography.Title>
            <Typography.Text type="secondary">
              Configure interview logistics and select interviewers for candidate interviews.
            </Typography.Text>
          </div>
        }
      >
        {/* Scrollable Card Body */}
        <div className="step5-card-body">
          {/* Interview Mode Selection */}
          <div style={{ marginBottom: 24 }}>
            <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 16, fontWeight: 600 }}>
              Interview Mode
            </Typography.Title>
            <InterviewModeSelector
              value={state.interviewMode}
              onChange={(mode) => handleStateChange({ interviewMode: mode })}
              disabled={isEditMode}
              onlineCardContent={
                interviewersRequired ? (
                  <div>
                    <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 16, fontWeight: 600 }}>
                      Interviewers <Typography.Text type="danger">*</Typography.Text>
                    </Typography.Title>
                    <InterviewerSelector
                      value={state.interviewers}
                      onChange={(interviewers) => handleStateChange({ interviewers })}
                      required={interviewersRequired}
                    />
                    {interviewersRequired && state.interviewers.length === 0 && (
                      <Alert
                        message="Please select at least one interviewer"
                        type="warning"
                        showIcon
                        style={{ marginTop: 16 }}
                      />
                    )}
                    
                    {/* External Guests - placed inside Online Interview card */}
                    <div style={{ marginTop: 24 }}>
                      <InterviewGuestManager
                        value={state.interviewGuests}
                        onChange={(guests) => handleStateChange({ interviewGuests: guests })}
                      />
                    </div>
                  </div>
                ) : undefined
              }
            />
          </div>

          {/* Validation Summary */}
          {!completionState.completed && (
            <Alert
              message="Please complete all required fields"
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </div>
        <div className="step5-card-footer">
          <StepFooter
            currentStep={currentStep}
            totalSteps={totalSteps}
            prompt={prompt}
            isGenerating={isGenerating}
            onPromptChange={onPromptChange}
            onGenerate={onGenerate}
            onPrevious={onPrevious}
            onNext={onNext}
            isPreviousDisabled={isPreviousDisabled}
            isNextDisabled={isNextDisabled}
            stepTitles={stepTitles}
            isNavigating={isNavigating}
            saveStatus={saveStatus}
          />
        </div>
      </Card>
    </div>
  );
};

Step5PostAssessment.displayName = 'Step5PostAssessment';

export default Step5PostAssessment;
